/**
 * Production adapter for `get_structure_properties`: the same sources the
 * structure details panel reads — the structure set read model
 * (`createStructureSetReadModel` over the session's structure set, read
 * live), `env.getRoiVolumeCm3` and `env.getRoiStatistics` (what
 * `useRoiStatistics` / the (i) popover use). Null-safe: no session needed.
 * Browser glue covered by the manual demo; the logic is unit-tested
 * through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { StructurePropertiesDeps } from './structureProperties';

export function useStructurePropertiesDeps(): () => StructurePropertiesDeps {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const session = useSessionOrNull();

    return useCallback((): StructurePropertiesDeps => {
        const uid = env?.structureSet?.sopInstanceUID;
        const live = uid
            ? session?.patient.getStructureSetWithSopInstanceUid(uid)
            : undefined;
        const model = live ? createStructureSetReadModel(live) : structureSet;
        return {
            structures: projectStructureFacts(structureSet, env),
            details: (n) => {
                const r = model?.roiArray.find((x) => x.roiNumber === n);
                return r
                    ? {
                          roiNumber: r.roiNumber,
                          name: r.name,
                          type: r.interpretedType,
                          approvalStatus: r.approvalStatus,
                          generationAlgorithm: r.roiGenerationAlgorithm,
                          description: r.roiDescription,
                          color: { R: r.color.R, G: r.color.G, B: r.color.B },
                          visible: r.visible,
                          isPointRoi: r.isPointRoi,
                          contourCount: r.contourCount,
                          ...(r.pointCoordinatesMm
                              ? { pointMm: Array.from(r.pointCoordinatesMm) }
                              : {}),
                          ...(r.electronDensityOverride !== undefined
                              ? {
                                    electronDensityOverride:
                                        r.electronDensityOverride,
                                }
                              : {}),
                          ...(r.massDensityOverride !== undefined
                              ? { massDensityOverride: r.massDensityOverride }
                              : {}),
                      }
                    : undefined;
            },
            stats: (n) => {
                const s = env?.getRoiStatistics(n);
                return s
                    ? {
                          surfaceAreaMm2: s.surfaceAreaMm2,
                          sphericity: s.sphericity,
                          centerOfMassMm: s.centerOfMassMm
                              ? Array.from(s.centerOfMassMm)
                              : null,
                          boundingBox: s.boundingBox
                              ? {
                                    min: Array.from(s.boundingBox.min),
                                    max: Array.from(s.boundingBox.max),
                                }
                              : null,
                      }
                    : undefined;
            },
            volumeCm3: (n) => env?.getRoiVolumeCm3(n) ?? Number.NaN,
        };
    }, [env, structureSet, session]);
}
